// Session persistence for in-progress file-share uploads.
//
// What's saved: the orderID, share-form settings (retention, access, etc.),
// and a fingerprint list of the files the user picked (NOT the file bytes —
// browsers can't persist a File handle across reloads). On reload the UI
// shows a "Resume previous upload?" banner; on confirm the user re-picks the
// files and tus-js-client continues each from its committed offset (its own
// urlStorage handles the byte-level resume — this store is just for the
// surrounding context).

const KEY = "transfer:pendingSession"

// Stale sessions (>7 days) are auto-discarded so localStorage doesn't fill up
// with abandoned uploads the server has already cleaned up.
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

function safeParse(raw) {
  try { return JSON.parse(raw) } catch { return null }
}

export function fingerprint(file) {
  return {
    name: file.name,
    size: file.size,
    type: file.type || "",
    lastModified: file.lastModified || 0,
  }
}

export function fileKey(fp) {
  return `${fp.name}:${fp.size}:${fp.lastModified}`
}

export function save({ orderID, entries, settings }) {
  if (typeof window === "undefined") return
  const data = {
    orderID,
    savedAt: Date.now(),
    files: entries.map(({ file, path }) => ({ ...fingerprint(file), path })),
    settings,
  }
  try { window.localStorage.setItem(KEY, JSON.stringify(data)) } catch {}
}

export function load() {
  if (typeof window === "undefined") return null
  const raw = window.localStorage.getItem(KEY)
  if (!raw) return null
  const data = safeParse(raw)
  if (!data || !data.orderID || !Array.isArray(data.files)) {
    clear(); return null
  }
  if (Date.now() - (data.savedAt || 0) > MAX_AGE_MS) {
    clear(); return null
  }
  return data
}

export function clear() {
  if (typeof window === "undefined") return
  try { window.localStorage.removeItem(KEY) } catch {}
}

// Patch an existing saved session in place — used when the user prunes the
// expected-files list during resume mode. `partial` is shallow-merged.
export function update(partial) {
  if (typeof window === "undefined") return null
  const current = load()
  if (!current) return null
  const next = { ...current, ...partial, savedAt: Date.now() }
  try { window.localStorage.setItem(KEY, JSON.stringify(next)) } catch {}
  return next
}

// Given a fresh File[] from a re-pick and a saved file list, return matched
// pairs plus a list of unmatched expected files. Match key: name + size +
// lastModified — same as tus-js-client's default fingerprint.
export function matchPickedFiles(pickedEntries, savedFiles) {
  const wanted = new Map(savedFiles.map(f => [fileKey(f), f]))
  const matched = []
  const extra = []
  for (const e of pickedEntries) {
    const k = fileKey(fingerprint(e.file))
    if (wanted.has(k)) {
      const saved = wanted.get(k)
      matched.push({ file: e.file, path: saved.path })
      wanted.delete(k)
    } else {
      extra.push(e)
    }
  }
  return { matched, missing: [...wanted.values()], extra }
}
